import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  BadgePercent,
} from "lucide-react";
import { discountService, promoService } from "@/lib/database";
import type { Discount, PromoCode } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AdminDiscountsPromos() {
  const [activeTab, setActiveTab] = useState("discounts");
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [promos, setPromos] = useState<PromoCode[]>([]);

  const [discountSearch, setDiscountSearch] = useState("");
  const [promoSearch, setPromoSearch] = useState("");

  const [discountPage, setDiscountPage] = useState(1);
  const [promoPage, setPromoPage] = useState(1);
  const itemsPerPage = 20;
  const [totalDiscountPages, setTotalDiscountPages] = useState(1);
  const [totalPromoPages, setTotalPromoPages] = useState(1);

  const [totalDiscounts, setTotalDiscounts] = useState(0);
  const [totalPromos, setTotalPromos] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    loadDiscounts();
  }, [discountPage]);

  useEffect(() => {
    loadPromos();
  }, [promoPage]);

  const loadDiscounts = async () => {
    const { items, total } = await discountService.getAll(
      itemsPerPage,
      (discountPage - 1) * itemsPerPage
    );
    setDiscounts(items);
    setTotalDiscountPages(Math.ceil(total / itemsPerPage));
    setTotalDiscounts(total);
  };

  const loadPromos = async () => {
    const { items, total } = await promoService.getAll(
      itemsPerPage,
      (promoPage - 1) * itemsPerPage
    );
    setPromos(items);
    setTotalPromoPages(Math.ceil(total / itemsPerPage));
    setTotalPromos(total);
  };

  const filteredDiscounts = discounts.filter(
    (d) =>
      d.code.toLowerCase().includes(discountSearch.toLowerCase()) ||
      d.description?.toLowerCase().includes(discountSearch.toLowerCase())
  );

  const filteredPromos = promos.filter(
    (p) =>
      p.code.toLowerCase().includes(promoSearch.toLowerCase()) ||
      p.description?.toLowerCase().includes(promoSearch.toLowerCase())
  );

  const renderItemCard = (
    item: Discount | PromoCode,
    type: "discount" | "promo"
  ) => {
    const typeLabel =
      type === "discount"
        ? (item as Discount).discount_type
        : (item as PromoCode).promo_type;

    const amount =
      typeLabel === "percentage" ? `${item.amount}%` : `$${item.amount}`;

    const startDate = item.start_date
      ? new Date(item.start_date).toLocaleDateString()
      : null;
    const endDate = item.end_date
      ? new Date(item.end_date).toLocaleDateString()
      : null;

    const usageLimit =
      type === "discount"
        ? (item as Discount).usage_limit_per_customer ?? "∞"
        : (item as PromoCode).usage_limit_per_customer ?? "∞";

    const totalUsage =
      type === "discount"
        ? (item as Discount).total_usage_limit ?? "∞"
        : (item as PromoCode).total_usage_limit ?? "∞";

    return (
      <div
        key={item.id}
        className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
      >
        <div className="flex items-center space-x-4">
          <BadgePercent className="h-5 w-5 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">{item.code}</p>
            <p className="text-sm text-gray-600">{item.description}</p>
          </div>
        </div>

        <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
          <div className="flex justify-between space-x-6">
            <div className="md:text-center">
              <p className="text-sm font-medium text-gray-900">Discount</p>
              <p className="text-xs text-gray-600">{amount}</p>
            </div>

            <div className="text-right md:text-center">
              <p className="text-sm font-medium text-gray-900">Stackable</p>
              <p className="text-xs text-gray-600">
                {item.is_stackable ? "Yes" : "No"}
              </p>
            </div>
          </div>

          <div className="flex my-2 md:my-0 justify-between space-x-6">
            {startDate && endDate && (
              <div className="md:text-center">
                <p className="text-sm font-medium text-gray-900">Validity</p>
                <p className="text-xs text-gray-600">
                  {startDate} - {endDate}
                </p>
              </div>
            )}

            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">Usage Limit</p>
              <p className="text-xs text-gray-600">{usageLimit}</p>
            </div>

            <div className="text-right md:text-center">
              <p className="text-sm font-medium text-gray-900">
                Total Usage Cap
              </p>
              <p className="text-xs text-gray-600">{totalUsage}</p>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => navigate(`/admin/${type}s/${item.id}`)}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        </div>
      </div>
    );
  };

  const renderPagination = (
    currentPage: number,
    totalPages: number,
    totalItems: number,
    onPageChange: (page: number) => void
  ) => (
    <div className="flex items-center justify-between mt-6 space-x-2">
      <p className="text-sm text-gray-600">
        Showing {(currentPage - 1) * itemsPerPage + 1}-
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
      </p>

      <div className="flex relative items-center justify-center pt-6 md:pt-0 space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        <span className="text-sm absolute md:relative top-0 text-gray-600">
          Page {currentPage} of {totalPages}
        </span>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between space-x-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Discounts & Promos
          </h1>
          <p className="text-gray-600">
            Manage and track all discounts and promo codes in the system
          </p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add New
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuItem onClick={() => navigate("/admin/discounts/new")}>
              New Discount
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/admin/promos/new")}>
              New Promo
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="discounts">Discounts</TabsTrigger>
          <TabsTrigger value="promos">Promos</TabsTrigger>
        </TabsList>

        <TabsContent value="discounts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between space-x-2">
                <div>
                  <CardTitle className="text-xl md:text-2xl">
                    Discounts ({totalDiscounts})
                  </CardTitle>
                  <CardDescription>
                    All discount codes across the platform
                  </CardDescription>
                </div>

                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search discounts..."
                    className="pl-10"
                    value={discountSearch}
                    onChange={(e) => setDiscountSearch(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredDiscounts.map((d) => renderItemCard(d, "discount"))}
              </div>
              {renderPagination(
                discountPage,
                totalDiscountPages,
                totalDiscounts,
                setDiscountPage
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promos" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between space-x-2">
                <div>
                  <CardTitle className="text-xl md:text-2xl">
                    Promos ({totalPromos})
                  </CardTitle>
                  <CardDescription>
                    All promo codes throughout the platform
                  </CardDescription>
                </div>

                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search promos..."
                    className="pl-10"
                    value={promoSearch}
                    onChange={(e) => setPromoSearch(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredPromos.map((p) => renderItemCard(p, "promo"))}
              </div>
              {renderPagination(
                promoPage,
                totalPromoPages,
                totalPromos,
                setPromoPage
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
